import { getSupabaseOrThrow } from '../supabaseClient'
import type { AccountAccess } from '../accountAccess'
import type { PassTier } from '../../data/placesIndex'

const PROFILE_COLUMNS =
  'id, full_name, origin, heard_about_us, inviter_email, inviter_email_set_at, show_points, membership_paid_at, hotpay_id_platnosci, pass_tier, membership_kwota, pass_upgraded_at, legacy_full_eligible, legacy_full_unlocked_at, stamp_notebook_waitlisted_at, account_access'

let profileInflight: Promise<Profile | null> | null = null

async function getCurrentUserId(): Promise<string | null> {
  const supabase = getSupabaseOrThrow()
  const { data } = await supabase.auth.getSession()
  return data?.session?.user?.id ?? null
}

export type Profile = {
  id: string
  full_name: string | null
  origin: string | null
  heard_about_us?: string | null
  inviter_email?: string | null
  inviter_email_set_at?: string | null
  show_points?: boolean
  /** Ustawiane przez Worker po HotPay SUCCESS */
  membership_paid_at?: string | null
  hotpay_id_platnosci?: string | null
  /** mini = 12 miejsc / sezon; full = 60 miejsc */
  pass_tier?: PassTier | null
  membership_kwota?: number | null
  pass_upgraded_at?: string | null
  /** Istniejący uczestnik — może odblokować 60 miejsc jednym kliknięciem */
  legacy_full_eligible?: boolean | null
  legacy_full_unlocked_at?: string | null
  stamp_notebook_waitlisted_at?: string | null
  /** full = pełny panel; places_preview = konto testowe (lista miejsc) */
  account_access?: AccountAccess | null
}
/** Profil po opłaconym wpisowym , bez tego nie zamykamy modalu powitalnego w panelu. */
export function isProfileOnboardingIncomplete(p: Profile | null): boolean {
  if (!p) return true
  if (p.account_access === 'places_preview') return false
  return !p.full_name?.trim() || !p.origin?.trim()
}

export async function getMyProfile(): Promise<Profile | null> {
  if (profileInflight) return profileInflight
  profileInflight = fetchMyProfile().finally(() => {
    profileInflight = null
  })
  return profileInflight
}

async function fetchMyProfile(): Promise<Profile | null> {
  const supabase = getSupabaseOrThrow()
  const userId = await getCurrentUserId()
  if (!userId) return null

  const { data, error } = await supabase.from('profiles').select(PROFILE_COLUMNS).eq('id', userId).maybeSingle()

  if (error) throw new Error(error.message)
  return (data as Profile | null) ?? null
}

export async function upsertMyProfile(input: {
  full_name: string
  origin: string
  /** Pominięcie , nie nadpisuje kolumny w bazie (np. sam zapis z ustawień bez zmiany „skąd”). */
  heard_about_us?: string | null
  /** Pominięcie , nie nadpisuje pola zapraszającego. */
  inviter_email?: string | null
  /** Pominięcie , nie nadpisuje preferencji widoczności punktów. */
  show_points?: boolean
}): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const userId = await getCurrentUserId()
  if (!userId) throw new Error('Brak zalogowanego użytkownika.')

  const payload: Record<string, unknown> = {
    id: userId,
    full_name: input.full_name.trim() || null,
    origin: input.origin.trim() || null,
  }
  if (input.heard_about_us !== undefined) {
    payload.heard_about_us = input.heard_about_us?.trim() || null
  }
  if (input.inviter_email !== undefined) {
    payload.inviter_email = input.inviter_email?.trim() || null
  }
  if (input.show_points !== undefined) {
    payload.show_points = Boolean(input.show_points)
  }

  const { error } = await supabase.from('profiles').upsert(payload, { onConflict: 'id' })
  if (error) throw new Error(error.message)
}

export async function unlockLegacyFullPass(): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const userId = await getCurrentUserId()
  if (!userId) throw new Error('Brak zalogowanego użytkownika.')

  const { data, error } = await supabase
    .from('profiles')
    .update({ legacy_full_unlocked_at: new Date().toISOString() })
    .eq('id', userId)
    .eq('legacy_full_eligible', true)
    .is('legacy_full_unlocked_at', null)
    .select('id')
    .maybeSingle()

  if (error) throw new Error(error.message)
  if (!data) throw new Error('Pełny Paszport jest już odblokowany lub konto nie kwalifikuje się.')
}

export async function joinStampNotebookWaitlist(): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const { data: sessionData } = await supabase.auth.getSession()
  const userId = sessionData?.session?.user?.id
  const email = sessionData?.session?.user?.email?.trim()
  if (!userId) throw new Error('Brak zalogowanego użytkownika.')

  const now = new Date().toISOString()
  const { data, error } = await supabase
    .from('profiles')
    .update({ stamp_notebook_waitlisted_at: now })
    .eq('id', userId)
    .is('stamp_notebook_waitlisted_at', null)
    .select('id')
    .maybeSingle()

  if (error) throw new Error(error.message)
  if (!data) return

  if (email) {
    const { error: waitlistError } = await supabase.from('waitlist_signups').insert({
      email: email.toLowerCase(),
      source: 'stamp_notebook',
    })
    if (waitlistError && waitlistError.code !== '23505') {
      throw new Error(waitlistError.message)
    }
  }
}
